import React from "react";
import { DefaultButton, Dropdown, PrimaryButton, Stack, TooltipHost } from "@fluentui/react";
import { FieldArray, Formik } from "formik";
import {
  addCustomerRHSTicket, updateCustomerRHSTicket,
} from "../../../services/customers/CustomerService";
import { IRHSTicketDetails } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import Datetime from "react-datetime";
import { Loading } from "../../../services/Loading";

const rhsTicketSchema = yup.object().shape({
    buildingId: yup.string().required("required"),
    ticketId: yup.string().required("required"),
    rhsPdfDataArray: yup.array().of(
        yup.object().shape({
            startDateTime: yup.mixed(),
            endDateTime: yup.mixed(),
            timeConsumed: yup.number(),
            billableHours: yup.number(),
            // billableHours: yup.number().required("Required"),
        })
    ),
});

export interface ManageAcDialogProps {
  data?: IRHSTicketDetails;
  handleDialogClose: any;
  customerId: string;
  datacenterId: string;
  buildingDict: any;
  date: Date;
}

const ManageRhsTicketsDialog: React.FC<ManageAcDialogProps> = ({
  data,
  handleDialogClose,
  customerId,
  datacenterId,
  buildingDict,
  date
}) => {
  //state
  const [state, setState] = React.useState({
    rhsTicket: {
      buildingId: "",
      ticketId: "",
      activityShortDescription: "",
      requestType: "Regular",
      rhsPdfDataArray: [
        {
            startDateTime: "",
            endDateTime: "",
            timeConsumed: undefined,
            billableHours: undefined
        }
      ],
      isManuallyAdded: true,
      customerId: customerId,
      datacenterId: datacenterId,
      date: date,
      ...data,
    },
    isLoading: false,
  } as {
    rhsTicket: IRHSTicketDetails;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);

  const closeAddDialog = (data?: IRHSTicketDetails) => {
    handleDialogClose(data);
  };

  const onStartDateChange = (date: any, index: number, values: IRHSTicketDetails) => {
      values.rhsPdfDataArray[index].startDateTime = date;
      setState({ ...state, rhsTicket: { ...state.rhsTicket, ...values }})
  }

  const onEndDateChange = (date: any, index: number, values: IRHSTicketDetails) => {
      values.rhsPdfDataArray[index].endDateTime = date;
      setState({ ...state, rhsTicket: { ...state.rhsTicket, ...values }})
  }

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <CustomDrawer
        title={
          state.rhsTicket._id
            ? "Update RHS Ticket"
            : "Add RHS Ticket"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={state.rhsTicket}
            validationSchema={rhsTicketSchema}
            onSubmit={async (
              values: IRHSTicketDetails,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let timeConsumed = 0, billableHours = 0;
                obj.rhsPdfDataArray = obj.rhsPdfDataArray.map((d) => {
                    // d.startDateTime = d.startDateTime ? new Date(d.startDateTime) : "",
                    // d.endDateTime = d.endDateTime ? new Date(d.endDateTime) : "",
                    d.billableHours = d.billableHours || 0;
                    d.timeConsumed = d.timeConsumed || 0;
                    timeConsumed += d.timeConsumed;
                    billableHours += d.billableHours;
                    return d;
                });
                obj.timeConsumed = timeConsumed;
                obj.billableHours = billableHours;
              setState({ ...state, isLoading: true });
              let result: any;
              if (state.rhsTicket._id) {
                result = await updateCustomerRHSTicket(obj);
              } else {
                result = await addCustomerRHSTicket(obj);
              }
              setState({ ...state, isLoading: false });
              if (result.success) {
                closeAddDialog({...obj, _id: result.data._id });
              } else {
                showToast(
                  result?.message ||
                    `Error while ${
                      state.rhsTicket._id ? "updating" : "adding"
                    } rhs ticket`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap}>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={Object.keys(buildingDict).map((d) => {
                          return {
                              key: d,
                              text: buildingDict[d]
                          }
                      })}
                      selectedKey={values.buildingId}
                      onChange={(e, item) => {
                        setFieldValue("buildingId", item?.key);
                      }}
                      label="Building Name"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.buildingId &&
                          touched.buildingId &&
                          errors.buildingId) as string
                      }
                      disabled={state.rhsTicket._id ? true : false}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"ticketId"} label={"RHS Ticket No"} disabled={state.rhsTicket._id ? true : false} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"activityShortDescription"} label={"Activity Short Description"} />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={["Regular", "Emergency"].map((d) => {
                          return {
                              key: d,
                              text: d
                          }
                      })}
                      selectedKey={values.requestType}
                      onChange={(e, item) => {
                        setFieldValue("requestType", item?.key);
                      }}
                      label="Request Type"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.requestType &&
                          touched.requestType &&
                          errors.requestType) as string
                      }
                      disabled={state.rhsTicket._id ? true : false}
                    />
                  </Stack.Item>
                  <Stack.Item>
                  <FieldArray name="rhsPdfDataArray">
                    {({ insert, remove }) => (
                      <Stack tokens={childrenGap}>
                          <Stack.Item>
                              <Stack horizontal horizontalAlign="end">
                                  <Stack.Item>
                                    <DefaultButton
                                      text="Add A Row"
                                      onClick={() => insert(0, { 
                                        startDateTime: "",
                                        endDateTime: "",
                                        timeConsumed: 0,
                                        billableHours: 0
                                      })}
                                      disabled={state.rhsTicket._id ? true : false}
                                    />
                                  {/* <TooltipHost
                                                    content="Add A Entry"
                                                    calloutProps={{
                                                      gapSpace: 0,
                                                    }}
                                                  >
                                                    <span
                                                      onClick={() => insert(0, { 
                                                      startDateTime: "",
                                                      endDateTime: "",
                                                      timeConsumed: 0,
                                                      billableHours: 0
                                                    })}
                                                    >
                                                      <Icon
                                                        path={mdiPlusBox}
                                                        size={0.7}
                                                        className="pointer"
                                                        color={
                                                          THEME.palette
                                                            .themePrimary
                                                        }
                                                      />
                                                    </span>
                                                  </TooltipHost>  */}
                                  </Stack.Item>
                              </Stack>
                          </Stack.Item>
                          <Stack.Item>
                              {values.rhsPdfDataArray.map((d, index) => (
                                <fieldset key={index}>
                                  <legend>{`Entry ${index + 1}`}</legend>
                                  <Stack wrap tokens={childrenGap} className={`${values.rhsPdfDataArray.length > 1 ? "" : "pt-8"}`}>
                                  {values.rhsPdfDataArray.length > 1 &&(<Stack.Item align="end">
                                  <DefaultButton
                                    text="Remove"
                                    onClick={() => remove(index)}
                                    className="borderless-btn"
                                    disabled={state.rhsTicket._id ? true : false}
                                  />
                                  </Stack.Item>)}
                                  <Stack.Item>
                                      <Stack wrap horizontal tokens={childrenGap}>
                                         <Stack.Item className="custom-datepicker position-relative" grow={1}>
                                             <label className="ms-Label">Start Date & Time</label>
                                                <Datetime
                                                    className="custom-field-group ms-TextField-fieldGroup "
                                                    inputProps={{
                                                    className: "ms-TextField-field px-8 border-0 w-100",
                                                    }}
                                                    dateFormat="YYYY-MM-DD"
                                                    timeFormat="HH:mm:ss"
                                                    value={d.startDateTime ? new Date(d.startDateTime) : ""}
                                                    onChange={(date) => {
                                                       onStartDateChange(date, index, values)
                                                    }}
                                                />
                                         </Stack.Item>
                                         <Stack.Item className="custom-datepicker position-relative" grow={1}>
                                             <label className="ms-Label">End Date & Time</label>
                                                <Datetime
                                                    className="custom-field-group ms-TextField-fieldGroup "
                                                    inputProps={{
                                                    className: "ms-TextField-field px-8 border-0 w-100",
                                                    }}
                                                    dateFormat="YYYY-MM-DD"
                                                    timeFormat="HH:mm:ss"
                                                    value={d.endDateTime ? new Date(d.endDateTime) : ""}
                                                    onChange={(date) => {
                                                       onEndDateChange(date, index, values)
                                                    }}
                                                />
                                         </Stack.Item>
                                      </Stack>
                                  </Stack.Item>
                                  <Stack.Item>
                                      <Stack wrap horizontal tokens={childrenGap}>
                                         <Stack.Item grow={1}>
                                            <CustomTextField name={`rhsPdfDataArray[${index}].timeConsumed`} label={"Time Consumed (Minutes)"} type="number" />
                                         </Stack.Item>
                                         <Stack.Item grow={1}>
                                            <CustomTextField name={`rhsPdfDataArray[${index}].billableHours`} label={"Billable Hours"} type="number" />
                                         </Stack.Item>
                                      </Stack>
                                  </Stack.Item>
                               </Stack>
                               </fieldset>
                              ))}
                          </Stack.Item>
                      </Stack>
                    )}
                  </FieldArray>   
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.rhsTicket._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageRhsTicketsDialog;
